import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  Tabs,
  Tab,
  Toolbar,
  Box,
} from '@mui/material';
import queryString from 'query-string';

import ReviewSubmissionsView from './ReviewSubmissionsView';
import ContentBox from '../components/layout/ContentBox';
import {
  RootState,
  AssignedReview,
} from '../redux/types';
import { setSelectedReviewTab, addTrialObjToStore, setLoading } from '../redux/actions';
import ApiClient from '../loopback/apiClient';
import withRouter, { RouteType } from '../hoc/withRouter';

type State = {
  assignments?: AssignedReview[];
  currentAssignmentId?: number;
}

class ReviewTrialsView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      assignments: undefined,
    };
  }

  componentDidMount() {
    const { userId, locale, setLoading: dispatchSetLoading } = this.props;
    if (userId) {
      dispatchSetLoading(true);
      ApiClient.getAssignedReviews(userId, locale)
        .then((assignedReviews) => this.setState({ assignments: assignedReviews }))
        .finally(() => {
          dispatchSetLoading(false);
        });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      setSelectedReviewTab: setReviewTabAction,
      addTrialObjToStore: addTrialToReduxActions,
      selectedTab,
      userId,
      locale,
      setLoading: dispatchSetLoading,
    } = this.props;
    const { userId: prevUserId } = prevProps;
    const { assignments } = this.state;
    if (userId && userId !== prevUserId) {
      dispatchSetLoading(true);
      ApiClient.getAssignedReviews(userId, locale)
        .then((assignedReviews) => this.setState({ assignments: assignedReviews }))
        .finally(() => dispatchSetLoading(false));
    }
    if (assignments && !selectedTab) {
      setReviewTabAction(assignments[0].id);
      addTrialToReduxActions(assignments[0].trial);
    }
  }

  getFirstAsisgnmentId = (): number | undefined => {
    const { assignments } = this.state;
    if (!assignments || !assignments.values.length) {
      return undefined;
    }
    return assignments[0].id;
  };

  render(): React.ReactNode {
    const {
      userId,
      selectedTab,
      setSelectedReviewTab: setSelectedTabAction,
      addTrialObjToStore: addTrialToReduxActions,
      router,
    } = this.props;
    const { assignments } = this.state;

    if (!userId || !assignments || !selectedTab) {
      return null;
    }

    const values = queryString.parse(router.location.search);
    const currentAssignmentId = selectedTab
      || (
        values.reviewedAssignment
          ? Number(values.reviewedAssignment)
          : this.getFirstAsisgnmentId()
      );

    const currentEnrollmentId = values.reviewedEnrollment && Number(values.reviewedEnrollment);
    return (
      <>
        {
          assignments
          && assignments
          && assignments.length > 0 && (
            <ContentBox
              elevation={0}
              title={(
                <FormattedMessage
                  id="review.assignedReviewsHeadline"
                  values={{ amount: assignments.length }}
                />
              )}
            >
              <Box flexGrow={1}>
                <Toolbar>
                  <Tabs
                    value={currentAssignmentId}
                    onChange={
                      (_event, id: number): void => {
                        setSelectedTabAction(id);
                        const selectedAssignment = assignments.find((asg) => asg.id === id);
                        if (selectedAssignment) {
                          addTrialToReduxActions(selectedAssignment?.trial);
                        }
                      }
                    }
                    scrollButtons="auto"
                    variant="scrollable"
                    indicatorColor="secondary"
                  >
                    {
                      assignments.map((assignment: AssignedReview) => (
                        <Tab
                          key={assignment.trial.code}
                          label={assignment.trial.code}
                          value={assignment.id}
                        />
                      ))
                    }
                  </Tabs>
                </Toolbar>
              </Box>
              <ReviewSubmissionsView
                assignment={
                  assignments.filter(
                    (assignment: AssignedReview) => assignment.id === currentAssignmentId,
                  )[0]
                }
                selectedEnrollment={currentEnrollmentId}
                navigate={router.navigate}
              />
            </ContentBox>
          )
        }
      </>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: RootState) => ({
  userId: state.users && state.users.token && state.users.token.userId,
  locale: state.intl.locale,
  selectedTab: state.review.selectedReviewTab,
});

const dispatchProps = {
  setSelectedReviewTab,
  addTrialObjToStore,
  setLoading,
};

type ReviewTrialViewProps = {
  userId?: number;
}

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = ReviewTrialViewProps & PropsFromRedux & RouteType;
export default connector(withRouter(ReviewTrialsView));
